import { useEffect, useRef } from 'react';
import maplibregl, { type RequestParameters } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MapPin } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useFraudHubGeoClusters } from '@/api/hooks/useFraudHubGeoClusters';
import { SEVERITY_FILL } from '@/lib/severity';
import { parsePeriod } from '@/lib/timePeriod';
import type { Severity } from '@/types/alerts';
import type { GeoCluster } from '@/types/fraudHub';

/**
 * Section 5 of the Fraud Hub: a station-centroid mini-map of the last
 * 7 days of open alerts.
 *
 * MapLibre setup mirrors the Maps page (`/osm/styles/osm-bright/style.json`
 * via the `/osm/*` Caddy proxy on the VPS, with a `transformStyle` that
 * rewrites relative sprite/glyph/tile URLs to absolute /osm-prefixed
 * URLs — MapLibre v5 silently drops sources whose tile URLs are
 * relative).
 *
 * Until the rule_engine starts populating `station_id` on alerts the
 * /geo-clusters endpoint returns `clusters: []`. We render an
 * informative empty state inside the Card body in that case (no map at
 * all — there's nothing to plot, so don't waste pixels on an empty
 * canvas).
 */

const STYLE_URL = '/osm/styles/osm-bright/style.json';
const INITIAL_CENTER: [number, number] = [28.0, -26.2]; // Johannesburg
const INITIAL_ZOOM = 5;
const NEUTRAL_FILL = 'hsl(215 20% 65%)';

function rewriteOsmUrl(rawUrl: string): string {
  try {
    if (rawUrl.startsWith('/osm/')) return rawUrl;
    if (rawUrl.startsWith('http')) {
      const u = new URL(rawUrl);
      if (u.pathname.startsWith('/osm/')) return rawUrl;
      if (u.pathname.startsWith('/tiles/')) return `/osm${u.pathname}${u.search}`;
      if (u.pathname.startsWith('/fonts/')) return `/osm${u.pathname}${u.search}`;
      if (u.pathname.startsWith('/styles/')) return `/osm${u.pathname}${u.search}`;
      return rawUrl;
    }
    if (rawUrl.startsWith('/tiles/')) return `/osm${rawUrl}`;
    if (rawUrl.startsWith('/fonts/')) return `/osm${rawUrl}`;
    if (rawUrl.startsWith('/styles/')) return `/osm${rawUrl}`;
    return rawUrl;
  } catch {
    return rawUrl;
  }
}

const transformRequest = (url: string): RequestParameters => ({
  url: rewriteOsmUrl(url),
});

function transformStyle(
  _previous: maplibregl.StyleSpecification | undefined,
  next: maplibregl.StyleSpecification,
): maplibregl.StyleSpecification {
  if (!next) return next;
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const out: maplibregl.StyleSpecification = { ...next };

  if (typeof out.sprite === 'string' && out.sprite.startsWith('/')) {
    out.sprite = `${origin}${out.sprite.startsWith('/osm/') ? '' : '/osm'}${out.sprite}`;
  }
  if (typeof out.glyphs === 'string' && out.glyphs.startsWith('/')) {
    out.glyphs = `${origin}${out.glyphs.startsWith('/osm/') ? '' : '/osm'}${out.glyphs}`;
  }

  if (out.sources) {
    const sources: Record<string, maplibregl.SourceSpecification> = { ...out.sources };
    const dropped = new Set<string>();
    for (const [id, src] of Object.entries(sources)) {
      if (
        src.type === 'geojson' &&
        typeof (src as { data?: unknown }).data === 'string' &&
        ((src as { data: string }).data.startsWith('/data/') ||
          (src as { data: string }).data.includes('/data/'))
      ) {
        delete sources[id];
        dropped.add(id);
      } else if (
        (src.type === 'vector' || src.type === 'raster') &&
        Array.isArray((src as { tiles?: unknown }).tiles)
      ) {
        const fixed = ((src as { tiles: string[] }).tiles).map((t) => {
          if (!t.startsWith('/')) return t;
          if (t.startsWith('/osm/')) return `${origin}${t}`;
          return `${origin}/osm${t}`;
        });
        sources[id] = { ...src, tiles: fixed } as maplibregl.SourceSpecification;
      }
    }
    if (dropped.size > 0) {
      out.layers = (out.layers ?? []).filter((l) => {
        const sourceId = (l as { source?: string }).source;
        return !sourceId || !dropped.has(sourceId);
      });
    }
    out.sources = sources;
  }

  return out;
}

function isKnownSeverity(s: string): s is Severity {
  return s === 'Critical' || s === 'High' || s === 'Medium'
      || s === 'Low'      || s === 'Watch' || s === 'Normal';
}

function markerColor(worst: string): string {
  return isKnownSeverity(worst)
    ? (SEVERITY_FILL[worst] ?? NEUTRAL_FILL)
    : NEUTRAL_FILL;
}

function markerRadius(alertCount: number): number {
  return Math.min(20, 4 + Math.log10(1 + alertCount) * 6);
}

export function GeoClusterMap(): JSX.Element {
  const [searchParams] = useSearchParams();
  const period = parsePeriod(searchParams.get('period'), 'M');
  const { data, isLoading, isError, error } = useFraudHubGeoClusters(period);
  const periodLabel = data?.period_label ?? '';

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">
          Geographic clusters{periodLabel ? ` — ${periodLabel}` : ''}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {isLoading && <Skeleton className="h-[360px] w-full rounded-md" />}

        {!isLoading && isError && (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
            Failed to load geo clusters: {error?.message ?? 'unknown error'}
          </div>
        )}

        {!isLoading && !isError && data && data.clusters.length === 0 && (
          <NoGeoData />
        )}

        {!isLoading && !isError && data && data.clusters.length > 0 && (
          <ClusterMap clusters={data.clusters} />
        )}
      </CardContent>
    </Card>
  );
}

function NoGeoData(): JSX.Element {
  return (
    <div className="flex h-[360px] flex-col items-center justify-center gap-3 rounded-md border border-dashed bg-muted/20 px-6 text-center">
      <MapPin className="h-10 w-10 text-muted-foreground/60" aria-hidden />
      <div>
        <p className="text-base font-semibold">No geo-tagged alerts yet</p>
        <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
          Alerts in this period don&apos;t carry station coordinates.
          The follow-up to populate <code className="font-mono text-xs">station_id</code>{' '}
          on alerts is in flight — this section will light up once it
          deploys.
        </p>
      </div>
    </div>
  );
}

function ClusterMap({ clusters }: { clusters: GeoCluster[] }): JSX.Element {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const navigate = useNavigate();

  // Initialise the map exactly once. Marker management is handled by
  // a second effect that re-runs when `clusters` changes.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: STYLE_URL,
      center: INITIAL_CENTER,
      zoom: INITIAL_ZOOM,
      transformRequest,
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    map.once('styledata', () => {
      // First style fetch may include relative URLs MapLibre rejects;
      // re-set the style with our transform to strip / rewrite them.
      map.setStyle(STYLE_URL, { transformStyle });
    });
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const attr = map as unknown as { __fbHubGeoMarkers?: maplibregl.Marker[] };
    const prev = attr.__fbHubGeoMarkers ?? [];
    prev.forEach((m) => m.remove());

    const next: maplibregl.Marker[] = [];
    const lngs: number[] = [];
    const lats: number[] = [];

    for (const c of clusters) {
      const radius = markerRadius(c.alert_count);
      const colour = markerColor(c.worst_severity);
      const el = document.createElement('div');
      el.style.cssText =
        `width:${radius * 2}px;height:${radius * 2}px;border-radius:50%;` +
        `background:${colour};border:2px solid white;box-shadow:0 1px 3px rgba(0,0,0,0.4);` +
        `cursor:pointer`;
      el.title = `${c.station_name ?? 'Station ' + c.station_id} — ${c.alert_count} alerts (${c.worst_severity})`;
      el.addEventListener('click', () => {
        navigate(`/fraud?station_id=${c.station_id}`);
      });
      const marker = new maplibregl.Marker({ element: el })
        .setLngLat([c.lon, c.lat])
        .addTo(map);
      next.push(marker);
      lngs.push(c.lon);
      lats.push(c.lat);
    }
    attr.__fbHubGeoMarkers = next;

    if (lngs.length > 0) {
      const minLng = Math.min(...lngs);
      const maxLng = Math.max(...lngs);
      const minLat = Math.min(...lats);
      const maxLat = Math.max(...lats);
      // fitBounds blows up on a single point — pad the box if degenerate.
      if (lngs.length === 1) {
        map.flyTo({ center: [lngs[0], lats[0]], zoom: 9, duration: 400 });
      } else {
        map.fitBounds(
          [
            [minLng, minLat],
            [maxLng, maxLat],
          ],
          { padding: 40, duration: 400, maxZoom: 11 },
        );
      }
    }
  }, [clusters, navigate]);

  return <div ref={containerRef} className="h-[360px] w-full overflow-hidden rounded-md" />;
}

export default GeoClusterMap;
